/**
 * @interface User
 * @description Representa a un usuario en el sistema.
 */
export interface User {
  /**
   * @property {number} id - El identificador único del usuario.
   */
  id: number;
  /**
   * @property {string} name - El nombre del usuario.
   */
  name: string;
  /**
   * @property {string} email - La dirección de correo electrónico del usuario.
   */
  email: string;
  /**
   * @property {string} role - El rol del usuario.
   */
  role: string;
  /**
   * @property {string[]} permissions - Los permisos del usuario.
   */
  permissions: string[];
  /**
   * @property {string} avatar - La URL de la imagen de perfil del usuario.
   */
  avatar?: string;
  /**
   * @property {Date} lastLogin - La fecha de la última sesión del usuario.
   */
  lastLogin?: Date;
  /**
   * @property {string} nombreCompleto - El nombre completo del usuario.
   */
  nombreCompleto: string;
  /**
   * @property {string} expiracion - La fecha de expiración del token en formato ISO 8601.
   */
  expiracion: string;
  /**
   * @property {number} [IdCorporativo] - El corporativo asociado cuando aplica.
   */
  idCorporativo: number;
  /**
   * @property {number} [IdEmpresa] - La empresa asociada cuando aplica.
   */
  idEmpresa: number;
}

/**
 * @interface LoginCredentials
 * @description Representa las credenciales requeridas para que un usuario inicie sesión.
 */
export interface LoginCredentials {
  /**
   * @property {string} user - El nombre de usuario.
   */
  user: string;
  /**
   * @property {string} password - La contraseña del usuario.
   */
  password: string;
}

/**
 * @interface LoginResponse
 * @description Representa la respuesta de la API de inicio de sesión.
 */
export interface LoginResponse {
  /**
   * @property {number} idUsuario - El ID del usuario.
   */
  IdUsuario: number;
  /**
   * @property {string} token - El token de autenticación.
   */
  Token: string;
  /**
   * @property {string} expiracion - La fecha de expiración del token en formato ISO 8601.
   */
  Expiracion: string;
  /**
   * @property {string[]} clavesMenu - Las claves del menú.
   */
  ClavesMenu: string[];
  /**
   * @property {string} rol - El rol del usuario.
   */
  Rol: string;
  /**
   * @property {string} nombreCompleto - El nombre completo.
   */
  NombreCompleto: string;
 
  /**
   * @property {boolean} [NuevoIngreso] - Si es true (primer ingreso / contraseña temporal), debe completarse el cambio de contraseña antes de usar la app.
   */
  NuevoIngreso?: boolean;
}

/**
 * Indica si el login exige cambiar la contraseña antes de continuar (compat. NuevoIngreso y RequiereCambioContrasena).
 */
export function loginRequiresMandatoryPasswordChange(
  response: LoginResponse
): boolean {
  return (
    response.NuevoIngreso === true
  );
}

/**
 * @interface AuthContextType
 * @description Define la forma del contexto de autenticación.
 */
export interface AuthContextType {
  /**
   * @property {User} user - El usuario del contexto de autenticación.
   */
  user: User | null;
  /**
   * @property {boolean} isAuthenticated - Indica si el usuario está autenticado.
   */
  isAuthenticated: boolean;
  /**
   * @property {boolean} isLoading - Indica si hay un proceso de autenticación en curso.
   */
  isLoading: boolean;
  /**
   * @property {function} login - La función de inicio de sesión del contexto de autenticación.
   */
  login: (credentials: LoginCredentials) => Promise<void>;
  /**
   * @property {function} logout - La función de cierre de sesión del contexto de autenticación.
   */
  logout: () => void;
  /**
   * @property {function} hasPermission - La función de verificación de permisos del contexto de autenticación.
   */
  hasPermission: (module: string) => boolean;
  /**
   * @property {function} getPermissions - La función de obtención de permisos del contexto de autenticación.
   */
  getPermissions: () => string[];
}

/**
 * @interface ModuleProps
 * @description Define las propiedades que se pueden pasar a un módulo remoto.
 */
export interface ModuleProps {
  /**
   * @property {User} user - El usuario del módulo.
   */
  user?: User;
  /**
   * @property {function} onNavigate - La función de navegación del módulo.
   */
  onNavigate?: (path: string) => void;
  /**
   * @property {string} theme - El tema del módulo.
   */
  theme?: 'light' | 'dark';
  /**
   * @property {string[]} permissions - Los permisos del módulo.
   */
  permissions?: string[];
}

/**
 * @interface ModuleInfo
 * @description Define la forma del módulo.
 */
export interface ModuleInfo {
  /**
   * @property {string} name - El nombre del módulo.
   */
  name: string;
  /**
   * @property {string} version - La versión del módulo.
   */
  version: string;
  /**
   * @property {string} lastUpdate - La fecha de la última actualización del módulo.
   */
  lastUpdate: string;
}

/**
 * @interface ApiResponse
 * @description Define la forma de la respuesta de la API.
 * @template T - El tipo de los datos de la respuesta.
 */
export interface ApiResponse<T = unknown> {
  /**
   * @property {T} data - Los datos de la respuesta.
   */
  data?: T;
  /**
   * @property {string} message - El mensaje de la respuesta.
   */
  message?: string;
  /**
   * @property {boolean} success - Indica si la respuesta fue exitosa.
   */
  success: boolean;
  /**
   * @property {string[]} errors - Los errores de la respuesta.
   */
  errors?: string[];
}

/**
 * @interface SSOApiResponse
 * @description Wrapper PascalCase devuelto por el backend SSO (Clean Architecture).
 * Usado en POST /api/Login/login — los datos reales están en `Data`.
 */
export interface SSOApiResponse<T> {
  Success: boolean;
  Message: string | null;
  Data: T;
  Errors: string[] | null;
  TraceId: string | null;
}

/**
 * @interface PaginatedResponse
 * @description Define la forma de la respuesta paginada.
 * @template T - El tipo de los datos de la respuesta.
 */
export interface PaginatedResponse<T = unknown> extends ApiResponse<T[]> {
  /**
   * @property {object} pagination - La información de la paginación.
   */
  pagination: {
    /**
     * @property {number} page - El número de la página.
     */
    page: number;
    /**
     * @property {number} limit - El límite de elementos por página.
     */
    limit: number;
    /**
     * @property {number} total - El total de elementos.
     */
    total: number;
    /**
     * @property {number} totalPages - El total de páginas.
     */
    totalPages: number;
  };
}

/**
 * @interface Invoice
 * @description Define la forma de la factura.
 */
export interface Invoice {
  /**
   * @property {string} id - El identificador único de la factura.
   */
  id: string;
  /**
   * @property {string} number - El número de folio o serie de la factura.
   */
  number: string;
  /**
   * @property {string} client - El cliente de la factura.
   */
  client: string;
  /**
   * @property {number} amount - El monto de la factura.
   */
  amount: number;
  /**
   * @property {Date} date - La fecha de la factura.
   */
  date: Date;
  /**
   * @property {string} status - El estado de la factura.
   */
  status: 'draft' | 'sent' | 'paid' | 'cancelled';
  /**
   * @property {string} template - El template de la factura.
   */
  template?: string;
}

/**
 * @interface InvoiceTemplate
 * @description Define la forma del template de la factura.
 */
export interface InvoiceTemplate {
  /**
   * @property {string} id - El identificador único del template.
   */
  id: string;
  /**
   * @property {string} name - El nombre del template.
   */
  name: string;
  /**
   * @property {string} content - El contenido del template.
   */
  content: string;
  /**
   * @property {boolean} isActive - Indica si el template está activo.
   */
  isActive: boolean;
  /**
   * @property {Date} createdAt - La fecha de creación del template.
   */
  createdAt: Date;
}

/**
 * @interface Expense
 * @description Define la forma del gasto.
 */
export interface Expense {
  /**
   * @property {string} id - El identificador único del gasto.
   */
  id: string;
  /**
   * @property {string} description - La descripción del gasto.
   */
  description: string;
  /**
   * @property {number} amount - El monto del gasto.
   */
  amount: number;
  /**
   * @property {string} category - La categoría del gasto.
   */
  category: string;
  /**
   * @property {Date} date - La fecha del gasto.
   */
  date: Date;
  /**
   * @property {string} status - El estado del gasto.
   */
  status: 'pending' | 'approved' | 'rejected';
  /**
   * @property {string} receipt - El recibo del gasto.
   */
  receipt?: string;
}

/**
 * @interface ExpenseCategory
 * @description Define la forma de la categoría del gasto.
 */
export interface ExpenseCategory {
  /**
   * @property {string} id - El identificador único de la categoría.
   */
  id: string;
  /**
   * @property {string} name - El nombre de la categoría.
   */
  name: string;
  /**
   * @property {string} description - La descripción de la categoría.
   */
  description?: string;
  /**
   * @property {boolean} isActive - Indica si la categoría está activa.
   */
  isActive: boolean;
  /**
   * @property {string} color - El color de la categoría.
   */
  color?: string;
}

/**
 * @interface Provider
 * @description Define la forma del proveedor.
 */
export interface Provider {
  /**
   * @property {string} id - El identificador único del proveedor.
   */
  id: string;
  /**
   * @property {string} name - El nombre del proveedor.
   */
  name: string;
  /**
   * @property {string} email - La dirección de correo electrónico del proveedor.
   */
  email: string;
  /**
   * @property {string} phone - El teléfono del proveedor.
   */
  phone?: string;
  /**
   * @property {string} address - La dirección del proveedor.
   */
  address?: string;
  /**
   * @property {string} contactPerson - La persona de contacto del proveedor.
   */
  contactPerson?: string;
  /**
   * @property {boolean} isActive - Indica si el proveedor está activo.
   */
  isActive: boolean;
  /**
   * @property {Date} createdAt - La fecha de creación del proveedor.
   */
  createdAt: Date;
}

/**
 * @interface NavigationItem
 * @description Define la forma del elemento de navegación.
 */
export interface NavigationItem {
  /**
   * @property {string} id - El identificador único del elemento de navegación.
   */
  id: string;
  /**
   * @property {string} label - El label del elemento de navegación.
   */
  label: string;
  /**
   * @property {string} path - La ruta del elemento de navegación.
   */
  path: string;
  /**
   * @property {string} icon - El icono del elemento de navegación.
   */
  icon?: string;
  /**
   * @property {string[]} permissions - Los permisos del elemento de navegación.
   */
  permissions?: string[];
  /**
   * @property {NavigationItem[]} children - Los elementos hijos del elemento de navegación.
   */
  children?: NavigationItem[];
}

/**
 * @interface ModuleError
 * @description Define el error de un módulo.
 */
export interface ModuleError {
  /**
   * @property {string} module - El nombre del módulo.
   */
  module: string;
  /**
   * @property {string} error - El error del módulo.
   */
  error: string;
  /**
   * @property {Date} timestamp - La fecha del error.
   */
  timestamp: Date;
  /**
   * @property {boolean} retryable - Indica si el error es reintentable.
   */
  retryable: boolean;
}

/**
 * @interface AppConfig
 * @description Define la configuración de la aplicación.
 */
export interface AppConfig {
  /**
   * @property {string} apiUrl - La URL de la API.
   */
  apiUrl: string;
  /**
   * @property {string} environment - El entorno de la aplicación.
   */
  environment: 'development' | 'staging' | 'production';
  /**
   * @property {object} features - Las características de la aplicación.
   */
  features: {
    /**
     * @property {boolean} autofactura - Indica si la autofactura está activada.
     */
    autofactura: boolean;
    /**
     * @property {boolean} gastos - Indica si los gastos están activados.
     */
    gastos: boolean;
    /**
     * @property {boolean} proveedores - Indica si los proveedores están activados.
     */
    proveedores: boolean;
  };
};
